import { useState, useEffect } from 'react';

interface FloatingCtaBarProps {
  onSignUp: () => void;
}

export default function FloatingCtaBar({ onSignUp }: FloatingCtaBarProps) {
  const [showCtaBar, setShowCtaBar] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!ctaDismissed) {
        setShowCtaBar(window.scrollY > 400);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [ctaDismissed]);

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
          showCtaBar && !ctaDismissed
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="relative flex items-center justify-between gap-4 px-8 py-4"
          style={{ background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 35%, #c026d3 65%, #ec4899 100%)' }}
        >
          {/* Animated shimmer overlay */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute inset-y-0 w-32 bg-white/10 skew-x-12"
              style={{ animation: 'floatingCtaShimmer 3s infinite', left: '-10%' }}
            />
          </div>

          {/* Left: badge + text + perks */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-xl bg-white/20 border border-white/30">
              <i className="ri-gift-2-line text-white text-xl"></i>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span
                  className="text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap"
                  style={{ background: 'rgba(255,220,0,0.25)', color: '#fde047', border: '1px solid rgba(253,224,71,0.4)' }}
                >
                  MEMBER EXCLUSIVE
                </span>
              </div>
              <p className="text-white font-black text-base leading-tight whitespace-nowrap">
                Sign Up Now — Unlock Exclusive Deals for Your Projects!
              </p>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-white/85 text-xs whitespace-nowrap">
                  <i className="ri-coupon-3-line text-yellow-300 text-xs"></i>
                  Special Discount Codes
                </span>
                <span className="text-white/40 text-xs">•</span>
                <span className="flex items-center gap-1 text-white/85 text-xs whitespace-nowrap">
                  <i className="ri-vip-crown-line text-yellow-300 text-xs"></i>
                  Early Access to New Features
                </span>
                <span className="text-white/40 text-xs">•</span>
                <span className="flex items-center gap-1 text-white/85 text-xs whitespace-nowrap">
                  <i className="ri-star-line text-yellow-300 text-xs"></i>
                  Exclusive Perks on 500+ Platforms
                </span>
              </div>
            </div>
          </div>

          {/* Right: CTA button + close */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={onSignUp}
              className="relative overflow-hidden bg-white text-fuchsia-700 font-black text-sm px-7 py-2.5 rounded-full whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
              style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
            >
              Claim Your Perks Now
              <i className="ri-arrow-right-line ml-1.5 text-sm"></i>
            </button>
            <button
              onClick={() => { setCtaDismissed(true); setShowCtaBar(false); }}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-white text-base"></i>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatingCtaShimmer {
          0% { left: -10%; }
          100% { left: 110%; }
        }
      `}</style>
    </>
  );
}
